import AddIcon from '@mui/icons-material/Add';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, alpha, Box, Button, Chip, Grid, IconButton, Skeleton, Stack, Theme, Tooltip, Typography, useTheme } from '@mui/material';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import BackNavigationButton from '../../../components/common/BackNavigationButton';
import LargeMaterialUISwitch from '../../../components/common/LargeMaterialUISwitch';
import PageContainer from '../../../components/common/PageContainter';
import StyledCard from '../../../components/common/StyledCard';
import StyledCardTitleBar from '../../../components/common/StyledCardTitleBar';
import MessageUsersFormEditiorPreview from '../../../components/services/messageUsers/sendMessage/MessageUsersFormEditiorPreview';
import useSubscriber from '../../../hooks/useSubscriber';
import { SettingsContext } from '../../../store/SettingsContext';
import { OnlyFansSubscriberHostessAutoReplyExpired } from '../../../types/hostessAutoReplyFreeloaders';
import {
    createEmptyOnlyFansSubscriberMessageUsersAutoMessageDetails,
    OnlyFansSubscriberMessageUsersAutoMessageDetails,
} from '../../../types/messageUsersAutoSendWelcomeMessage';
import { isSextforceActive, stripHtmlAndConvertParagraphs } from '../../../utils/common';
import AutoReplyToExpiredIntroCard from './components/AutoReplyToExpiredIntroCard';
import AutoReplyToExpiredSettingsCard from './components/AutoReplyToExpiredSettingsCard';
import HostessAutoReplyFreeloadersMessageDetailsDialog from './components/HostessAutoReplyFreeloadersMessageDetailsDialog';
import SettingsSectionHeader from './components/SettingsSectionHeader';
import { normalizeFreeloadersReplyToMessageDetails } from './freeloadersReplyUtils';

const DEFAULT_EXPIRED_FORM: OnlyFansSubscriberHostessAutoReplyExpired = {
    action: 'mute',
    delayBeforeSendingMessage: false,
    active: false,
    autoUnrestrict: true,
    excludeCreators: true,
    sendReplyOnPurchase: false,
    skipUsersnames: [],
    replies: [],
    repeatScriptWhenFinished: false,
    historyTtlDays: null,
};

const expiredSettingsCompareKey = (s: OnlyFansSubscriberHostessAutoReplyExpired): string => {
    const skip = [...(s.skipUsersnames || [])].sort((a, b) => a.localeCompare(b));
    const repliesNormalized = s.replies.map(r => normalizeFreeloadersReplyToMessageDetails(r as any));
    const repliesRawFingerprint = s.replies.map(r =>
        typeof r === 'string'
            ? { t: 's' as const, m: r, mediaIds: [] as number[] }
            : {
                  t: 'd' as const,
                  m: r?.message ?? '',
                  mediaIds: (r?.mediaFiles ?? []).map(m => m.id),
                  lockMessagePrice: r?.lockMessagePrice ?? null,
                  lockedText: r?.lockedText ?? false,
              },
    );

    // `active` is deliberately excluded: the running state is owned by the on/off toggle
    // (`setServiceRunning`), so a toggle must neither mark the form dirty nor block hydration.
    return JSON.stringify({
        delayBeforeSendingMessage: s.delayBeforeSendingMessage,
        action: s.action,
        excludeCreators: s.excludeCreators,
        autoUnrestrict: s.autoUnrestrict,
        sendReplyOnPurchase: s.sendReplyOnPurchase,
        repeatScriptWhenFinished: s.repeatScriptWhenFinished ?? false,
        historyTtlDays: s.historyTtlDays ?? null,
        skipUsersnames: skip,
        repliesNormalized,
        repliesRawFingerprint,
    });
};

const HostessAutoReplyToExpired = () => {
    const settingsContext = useContext(SettingsContext);
    const {
        data: subscriber,
        isFetching: subscriberLoading,
        setServiceRunning,
        setServiceRunningLoading,
        setServiceSettingsHostessAutoReplyExpired,
        setServiceSettingsHostessAutoReplyExpiredLoading,
    } = useSubscriber();
    const theme: Theme = useTheme();
    const dialog = useDialog();
    const { enqueueSnackbar } = useSnackbar();

    const autoReplyExpired: OnlyFansSubscriberHostessAutoReplyExpired | null = useMemo(() => {
        return subscriber && subscriber.hostess && subscriber.hostess.autoReply && subscriber.hostess.autoReply.expired
            ? subscriber.hostess.autoReply.expired
            : null;
    }, [subscriber]);

    const [formData, setFormData] = useState<OnlyFansSubscriberHostessAutoReplyExpired>(autoReplyExpired || DEFAULT_EXPIRED_FORM);
    const formDataRef = useRef(formData);
    formDataRef.current = formData;

    const hydratedForSubscriberIdRef = useRef<string | null>(null);
    const [autoReplyExpiredRunning, setAutoReplyExpiredServiceRunning] = useState<boolean>(false);

    const [historyTtlEnabled, setHistoryTtlEnabled] = useState<boolean>(false);
    const [historyTtlDaysString, setHistoryTtlDaysString] = useState<string>(
        autoReplyExpired && autoReplyExpired.historyTtlDays ? autoReplyExpired.historyTtlDays.toString() : '0',
    );

    useEffect(() => {
        if (autoReplyExpired && subscriber && '_id' in subscriber && subscriber._id) {
            const subId = String(subscriber._id);
            const serverBlock: OnlyFansSubscriberHostessAutoReplyExpired = {
                ...autoReplyExpired,
                replies: autoReplyExpired.replies.map(reply => {
                    if (typeof reply === 'string') {
                        return stripHtmlAndConvertParagraphs(reply);
                    }

                    return {
                        ...reply,
                        message: reply.message ?? '',
                    };
                }),
            };

            setFormData(prev => {
                const switchedSubscriber = hydratedForSubscriberIdRef.current !== subId;
                hydratedForSubscriberIdRef.current = subId;

                if (switchedSubscriber) {
                    return serverBlock;
                }

                const prevMatchesDefaultForm = expiredSettingsCompareKey(prev) === expiredSettingsCompareKey(DEFAULT_EXPIRED_FORM);
                if (prevMatchesDefaultForm) {
                    return serverBlock;
                }

                const incomingMatchesLocal = expiredSettingsCompareKey(prev) === expiredSettingsCompareKey(serverBlock);
                if (!incomingMatchesLocal) {
                    // Preserve local draft edits while a refetch is in flight.
                    return prev;
                }

                return serverBlock;
            });

            if (autoReplyExpired.historyTtlDays) {
                setHistoryTtlEnabled(true);
                setHistoryTtlDaysString(autoReplyExpired.historyTtlDays.toString());
            } else {
                setHistoryTtlEnabled(false);
                setHistoryTtlDaysString('0');
            }

            setAutoReplyExpiredServiceRunning(autoReplyExpired.active && isSextforceActive(subscriber) ? true : false);
        } else {
            setAutoReplyExpiredServiceRunning(false);
        }
    }, [autoReplyExpired, subscriber]);

    const hydratedAutoReplyExpiredForState = useMemo(() => {
        if (!autoReplyExpired) {
            return DEFAULT_EXPIRED_FORM;
        }

        return {
            ...autoReplyExpired,
            replies: autoReplyExpired.replies.map(reply => {
                if (typeof reply === 'string') {
                    return stripHtmlAndConvertParagraphs(reply);
                }

                return {
                    ...reply,
                    message: reply.message ?? '',
                };
            }),
        };
    }, [autoReplyExpired]);

    const hasUnsavedChanges = useMemo(() => {
        return expiredSettingsCompareKey(formData) !== expiredSettingsCompareKey(hydratedAutoReplyExpiredForState);
    }, [formData, hydratedAutoReplyExpiredForState]);

    const anyReplyEmpty = formData.replies.some(reply => {
        if (typeof reply === 'string') {
            return reply.trim() === '';
        }

        return stripHtmlAndConvertParagraphs(reply.message || '').trim() === '';
    });

    const moveReply = (fromIndex: number, toIndex: number) => {
        setFormData(prev => {
            if (toIndex < 0 || toIndex >= prev.replies.length || fromIndex === toIndex) {
                return prev;
            }

            const replies = [...prev.replies];
            const [movedReply] = replies.splice(fromIndex, 1);
            if (!movedReply) {
                return prev;
            }

            replies.splice(toIndex, 0, movedReply);
            return { ...prev, replies };
        });
    };

    const duplicateReply = (index: number) => {
        setFormData(prev => {
            const item = prev.replies[index];
            if (item === undefined) {
                return prev;
            }
            const replies = [...prev.replies];
            replies.splice(index + 1, 0, typeof item === 'string' ? item : { ...item });
            return { ...prev, replies };
        });
    };

    // Drag-and-drop reordering of replies (initiated from the drag handle only)
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const dragFromHandleRef = useRef(false);

    const [messageDialogOpen, setMessageDialogOpen] = useState(false);
    const [messageDialogIndex, setMessageDialogIndex] = useState<number | null>(null);
    const [messageDialogTitle, setMessageDialogTitle] = useState('Edit reply message');
    const [messageDialogSessionKey, setMessageDialogSessionKey] = useState(0);
    /** Stable placeholder when the dialog is closed. */
    const expiredDialogClosedDetailsRef = useRef(createEmptyOnlyFansSubscriberMessageUsersAutoMessageDetails());
    /**
     * Snapshot taken at the moment the user opens the dialog so the editor always matches the current reply.
     * This prevents stale Quill/DraftJS state when the parent re-renders.
     */
    const [dialogEditSnapshot, setDialogEditSnapshot] = useState<OnlyFansSubscriberMessageUsersAutoMessageDetails | null>(null);

    const dialogInitialMessageDetails =
        messageDialogOpen && dialogEditSnapshot !== null ? dialogEditSnapshot : expiredDialogClosedDetailsRef.current;

    const openEditDialog = (index: number) => {
        const reply = formDataRef.current.replies[index];
        if (reply === undefined) {
            return;
        }

        setDialogEditSnapshot(normalizeFreeloadersReplyToMessageDetails(reply as any));
        setMessageDialogTitle(`Reply ${index + 1}`);
        setMessageDialogIndex(index);
        setMessageDialogSessionKey(k => k + 1);
        setMessageDialogOpen(true);
    };

    const handleSaveMessageFromDialog = (details: OnlyFansSubscriberMessageUsersAutoMessageDetails, replyIndex: number) => {
        if (replyIndex < 0) {
            return;
        }

        setFormData(prev => {
            const replies = [...prev.replies];
            while (replies.length <= replyIndex) {
                replies.push(createEmptyOnlyFansSubscriberMessageUsersAutoMessageDetails());
            }
            replies[replyIndex] = details;
            return { ...prev, replies };
        });
    };

    return (
        <PageContainer>
            {subscriber && '_id' in subscriber && subscriber._id && (
                <>
                    <Typography variant="h5" sx={{ marginBottom: theme.spacing(2) }}>
                        <BackNavigationButton url={`/subscribers/${subscriber._id}/${settingsContext.services.messageUsers.homeUrl}`} />{' '}
                        Hostess Auto Reply to Expired for {subscriber.username}
                    </Typography>

                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title="Auto Reply to Expired" />
                    </StyledCard>

                    <AutoReplyToExpiredIntroCard />

                    {!subscriber.isLoggedIn && (
                        <Alert severity="error" sx={{ marginBottom: theme.spacing(2) }}>
                            We need to relogin to this account
                            <br />
                            Please contact Admin
                        </Alert>
                    )}

                    <StyledCard>
                        <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center">
                            <Grid item xs>
                                <Stack direction="row" spacing={1.25} alignItems="center">
                                    <Box
                                        sx={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: '50%',
                                            flexShrink: 0,
                                            bgcolor: autoReplyExpiredRunning ? 'success.main' : 'error.main',
                                        }}
                                    />
                                    <div>
                                        Auto Reply to Expired is currently{' '}
                                        {autoReplyExpiredRunning ? (
                                            <span style={{ color: theme.palette.success.dark, fontWeight: 600 }}>running</span>
                                        ) : (
                                            <span style={{ color: theme.palette.error.main, fontWeight: 600 }}>not running</span>
                                        )}
                                        .
                                    </div>
                                </Stack>
                            </Grid>
                            <Grid item xs="auto">
                                {subscriberLoading || setServiceRunningLoading ? (
                                    <Skeleton width={87} height={51} />
                                ) : (
                                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ maxWidth: { xs: '100%', sm: 360 } }}>
                                        <LargeMaterialUISwitch
                                            checked={autoReplyExpiredRunning}
                                            disabled={!autoReplyExpired || !autoReplyExpired.replies || !autoReplyExpired.replies.length}
                                            onChange={e => {
                                                setServiceRunning('hostess.autoReply.expired', e.target.checked);
                                            }}
                                            color="primary"
                                        />
                                        {formData.replies.length === 0 && (
                                            <Typography variant="body2" color="warning.main" sx={{ lineHeight: 1.35 }}>
                                                At least one reply is required to turn the system on. Add replies below, then save.
                                            </Typography>
                                        )}
                                    </Stack>
                                )}
                            </Grid>
                        </Grid>
                    </StyledCard>

                    <AutoReplyToExpiredSettingsCard
                        formData={formData}
                        setFormData={setFormData}
                        autoReplyExpired={autoReplyExpired}
                        historyTtlEnabled={historyTtlEnabled}
                        setHistoryTtlEnabled={setHistoryTtlEnabled}
                        historyTtlDaysString={historyTtlDaysString}
                        setHistoryTtlDaysString={setHistoryTtlDaysString}
                    />

                    <StyledCard>
                        <SettingsSectionHeader icon={ForumOutlinedIcon} title="Replies" />

                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                            These are the automatic replies the system will send to the expired fans if they match the above criteria. You
                            can have setup as many replies as you like. On the last reply, the system will take the action you specified
                            above. Try to nudge the expired fans <strong>politely</strong> towards resubscribing or following you again.
                        </Typography>

                        <Stack spacing={1.5}>
                            {formData.replies.length === 0 && (
                                <Typography variant="body1" align="center" sx={{ color: 'text.secondary', py: 2 }}>
                                    No replies
                                </Typography>
                            )}
                            {formData.replies.map((item, index) => {
                                const tagsCount =
                                    typeof item === 'string'
                                        ? 0
                                        : (item.taggedCreators?.length || 0) + (item.taggedReleaseForms?.length || 0);
                                const isEmpty =
                                    typeof item === 'string'
                                        ? item.trim() === ''
                                        : stripHtmlAndConvertParagraphs(item.message || '').trim() === '';

                                return (
                                    <Box
                                        key={index}
                                        draggable
                                        onDragStart={e => {
                                            if (!dragFromHandleRef.current) {
                                                e.preventDefault();
                                                return;
                                            }
                                            dragFromHandleRef.current = false;
                                            e.dataTransfer.effectAllowed = 'move';
                                            setDragIndex(index);
                                        }}
                                        onDragOver={e => {
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = 'move';
                                            if (dragOverIndex !== index) {
                                                setDragOverIndex(index);
                                            }
                                        }}
                                        onDragLeave={() => {
                                            if (dragOverIndex === index) {
                                                setDragOverIndex(null);
                                            }
                                        }}
                                        onDrop={e => {
                                            e.preventDefault();
                                            if (dragIndex !== null) {
                                                moveReply(dragIndex, index);
                                            }
                                            setDragIndex(null);
                                            setDragOverIndex(null);
                                        }}
                                        onDragEnd={() => {
                                            dragFromHandleRef.current = false;
                                            setDragIndex(null);
                                            setDragOverIndex(null);
                                        }}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 1.5,
                                            border: '1px solid',
                                            borderColor: dragOverIndex === index && dragIndex !== index ? 'primary.main' : 'divider',
                                            bgcolor: 'background.paper',
                                            opacity: dragIndex === index ? 0.5 : 1,
                                            transition: 'border-color 0.15s ease, opacity 0.15s ease',
                                        }}
                                    >
                                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                                            <Box
                                                onMouseDown={() => {
                                                    dragFromHandleRef.current = true;
                                                }}
                                                onTouchStart={() => {
                                                    dragFromHandleRef.current = true;
                                                }}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    color: 'text.disabled',
                                                    cursor: 'grab',
                                                    '&:active': { cursor: 'grabbing' },
                                                }}
                                                aria-label="Drag to reorder"
                                            >
                                                <DragIndicatorIcon fontSize="small" />
                                            </Box>
                                            <Typography variant="subtitle2">Reply {index + 1}</Typography>
                                            {tagsCount > 0 && typeof item !== 'string' && (
                                                <Tooltip
                                                    title={[...(item.taggedCreators || []), ...(item.taggedReleaseForms || [])]
                                                        .map(tag => tag.name)
                                                        .join(', ')}
                                                    placement="top"
                                                >
                                                    <Chip
                                                        icon={<AlternateEmailIcon />}
                                                        label={tagsCount > 1 ? `${tagsCount} Tags` : 'Tags'}
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        sx={{ height: 22 }}
                                                    />
                                                </Tooltip>
                                            )}
                                            <Box sx={{ flexGrow: 1 }} />
                                            <Stack
                                                direction="row"
                                                spacing={0}
                                                flexWrap="wrap"
                                                alignItems="center"
                                                justifyContent="flex-end"
                                                sx={{ gap: theme.spacing(0.25) }}
                                            >
                                                <Tooltip title="Move reply up" placement="top">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            disabled={index === 0}
                                                            onClick={() => moveReply(index, index - 1)}
                                                            aria-label="Move reply up"
                                                        >
                                                            <KeyboardArrowUpIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Move reply down" placement="top">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            disabled={index === formData.replies.length - 1}
                                                            onClick={() => moveReply(index, index + 1)}
                                                            aria-label="Move reply down"
                                                        >
                                                            <KeyboardArrowDownIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Edit reply" placement="top">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => openEditDialog(index)}
                                                            aria-label="Edit reply"
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Duplicate reply" placement="top">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => duplicateReply(index)}
                                                            aria-label="Duplicate reply"
                                                        >
                                                            <ContentCopyIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Delete reply" placement="top">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => {
                                                                dialog
                                                                    .confirm({
                                                                        title: 'Delete Reply',
                                                                        message: `Are you sure you want to delete this reply ${index + 1}?`,
                                                                    })
                                                                    .then(() => {
                                                                        setFormData(prev => {
                                                                            const replies = [...prev.replies];
                                                                            replies.splice(index, 1);
                                                                            return { ...prev, replies };
                                                                        });
                                                                    })
                                                                    .catch(() => {});
                                                            }}
                                                            aria-label="Delete reply"
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Stack>
                                        </Stack>
                                        {isEmpty ? (
                                            <em style={{ color: theme.palette.text.disabled }}>Empty — click Edit</em>
                                        ) : (
                                            <MessageUsersFormEditiorPreview
                                                message={typeof item === 'string' ? item : item.message}
                                                lockMessage={typeof item === 'string' ? false : item.lockMessagePrice ? true : false}
                                                lockMessagePrice={typeof item === 'string' ? 0 : item.lockMessagePrice || 0}
                                                lockedText={typeof item === 'string' ? false : item.lockedText || false}
                                                taggedCreators={typeof item === 'string' ? [] : item.taggedCreators || []}
                                                taggedReleaseForms={typeof item === 'string' ? [] : item.taggedReleaseForms || []}
                                                showTags={false}
                                                fullWidth={true}
                                                theme={theme}
                                            />
                                        )}
                                    </Box>
                                );
                            })}

                            <Button
                                fullWidth
                                variant="outlined"
                                color="primary"
                                startIcon={<AddIcon />}
                                sx={t => ({
                                    borderStyle: 'dashed',
                                    borderWidth: 1.5,
                                    py: 1.25,
                                    '&:hover': {
                                        borderStyle: 'dashed',
                                        borderWidth: 1.5,
                                        bgcolor: alpha(t.palette.primary.main, 0.04),
                                    },
                                })}
                                onClick={() => {
                                    flushSync(() => {
                                        setFormData(prev => ({
                                            ...prev,
                                            replies: [...prev.replies, createEmptyOnlyFansSubscriberMessageUsersAutoMessageDetails()],
                                        }));
                                    });
                                    const newIndex = formDataRef.current.replies.length - 1;
                                    const reply = formDataRef.current.replies[newIndex];
                                    if (reply === undefined) {
                                        return;
                                    }
                                    setDialogEditSnapshot(normalizeFreeloadersReplyToMessageDetails(reply as any));
                                    setMessageDialogTitle(`Reply ${newIndex + 1}`);
                                    setMessageDialogIndex(newIndex);
                                    setMessageDialogSessionKey(k => k + 1);
                                    setMessageDialogOpen(true);
                                }}
                            >
                                Add Reply
                            </Button>
                        </Stack>
                    </StyledCard>

                    <StyledCard noCard>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1.5}
                            flexGrow={0}
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                            justifyContent="center"
                            sx={{ width: '100%' }}
                        >
                            <LoadingButton
                                fullWidth
                                variant="contained"
                                color={hasUnsavedChanges ? 'secondary' : 'primary'}
                                size="large"
                                loading={setServiceSettingsHostessAutoReplyExpiredLoading}
                                disabled={!formData || (historyTtlEnabled && !formData.historyTtlDays) || anyReplyEmpty}
                                onClick={() => {
                                    if (formData) {
                                        dialog
                                            .confirm({
                                                title: 'Save Changes',
                                                message:
                                                    'Are you sure you want to save these changes? All the replies history will be reset and start from scrach.',
                                                ok: {
                                                    text: 'Yes',
                                                    variant: 'contained',
                                                    color: 'primary',
                                                },
                                                cancel: {
                                                    text: 'No',
                                                    variant: 'contained',
                                                    color: 'error',
                                                },
                                            })
                                            .then(() => {
                                                // `active` is owned by the on/off toggle — always send the server's
                                                // current value so saving settings can't silently stop the service.
                                                setServiceSettingsHostessAutoReplyExpired({
                                                    ...formData,
                                                    active: autoReplyExpired?.active ?? false,
                                                }).then(data => {
                                                    if (data) {
                                                        enqueueSnackbar('Settings saved successfully', { variant: 'success' });
                                                    } else {
                                                        enqueueSnackbar('Failed to save settings', { variant: 'error' });
                                                    }
                                                });
                                            })
                                            .catch(() => {});
                                    }
                                }}
                                startIcon={<SaveIcon />}
                            >
                                <span>Save Changes</span>
                            </LoadingButton>

                            <Button
                                fullWidth
                                variant="outlined"
                                color="secondary"
                                size="large"
                                disabled={!hasUnsavedChanges}
                                onClick={() => {
                                    setFormData(DEFAULT_EXPIRED_FORM);
                                    setHistoryTtlEnabled(false);
                                    setHistoryTtlDaysString('0');
                                    setMessageDialogOpen(false);
                                    setMessageDialogIndex(null);
                                    setDialogEditSnapshot(null);
                                }}
                                startIcon={<RestartAltIcon />}
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                Reset
                            </Button>
                        </Stack>
                    </StyledCard>

                    <HostessAutoReplyFreeloadersMessageDetailsDialog
                        key={messageDialogSessionKey}
                        subscriber={subscriber}
                        open={messageDialogOpen}
                        replyIndex={messageDialogIndex ?? -1}
                        onClose={() => {
                            setMessageDialogOpen(false);
                            setMessageDialogIndex(null);
                            setDialogEditSnapshot(null);
                        }}
                        initialMessageDetails={dialogInitialMessageDetails}
                        theme={theme}
                        title={messageDialogTitle}
                        onSave={handleSaveMessageFromDialog}
                    />
                </>
            )}
        </PageContainer>
    );
};

export default HostessAutoReplyToExpired;
